import {ARGS} from "../../../bootstrap/args";

// WSC/Blaze player bridge. The floating button's thumbnail comes straight from the game feed, so
// Blaze is only needed to PLAY. We lazily code-split the ~1.2MB SDK, initialize it once, and open
// the native full-screen story player via the SDK's `playContent` API. `prepare` warms the SDK when
// the button appears so `open` plays quickly on click; both fall back to the story url on failure.
const WSC = {
  // Publishable client key (ships in the bundle by design). Env-scoped + rotatable.
  // Resolution: env (REACT_APP_BLAZE_API_KEY) -> the key already committed on this branch.
  API_KEY: ARGS.BLAZE_API_KEY,
  CONNECT_EVENT: "onBlazeSDKConnect",
  CONTENT_TYPE: "story",
};

// Per the Blaze docs the SDK exposes no ready/error status: Initialize() resolves, then it fires
// onBlazeSDKConnect once it's actually usable. onBlazeSDKConnect is therefore the only readiness
// signal, and a timeout is the only way to detect "never connected" -> fall back to the url.
const CONNECT_TIMEOUT_MS = 8000;

let sdkPromise = null;
let readyPromise = null;

const loadSdk = () => {
  if (!sdkPromise) {
    sdkPromise = import('@wscsports/blaze-web-sdk' /* webpackChunkName: "blaze-web-sdk" */)
      .then((mod) => mod.default || mod);
  }
  return sdkPromise;
};

// Resolves once the SDK is initialized AND connected; rejects if Initialize() fails or the connect
// event never arrives. Cached so we init once; cleared on failure so a later click can retry.
const ensureReady = (BlazeSDK, opts) => {
  if (!readyPromise) {
    readyPromise = new Promise((resolve, reject) => {
      const onConnect = () => {
        clearTimeout(timer);
        resolve();
      };
      const timer = setTimeout(() => {
        document.removeEventListener(WSC.CONNECT_EVENT, onConnect);
        reject(new Error('blaze connect timeout'));
      }, CONNECT_TIMEOUT_MS);

      document.addEventListener(WSC.CONNECT_EVENT, onConnect, {once: true});

      // Initialize() is async, so its rejection surfaces here (not a sync throw).
      Promise.resolve(BlazeSDK.Initialize(WSC.API_KEY, {
        layoutDirection: opts.isRtl ? 'RTL' : 'LTR',
        preferredLanguage: opts.cultureName,
      })).catch((e) => {
        clearTimeout(timer);
        document.removeEventListener(WSC.CONNECT_EVENT, onConnect);
        reject(e);
      });
    });

    readyPromise.catch(() => { readyPromise = null; });
  }

  return readyPromise;
};

const openFallback = (story) => {
  if (story?.url) {
    window.open(story.url, '_blank', 'noopener');
  }
};

// Warm the SDK (fetch chunk + initialize) when the button appears, so the click plays fast.
export const prepareWscPlayer = (story, opts = {}) => {
  if (!story?.id) return;

  loadSdk()
    .then((BlazeSDK) => ensureReady(BlazeSDK, opts))
    .catch(() => {});
};

// Open the native WSC/Blaze story player for the feed story; fall back to the url on any failure
// (SDK load, Initialize, connect timeout, or playContent).
export const openWscPlayer = (story, opts = {}) => {
  if (!story?.id) return;

  loadSdk()
    .then((BlazeSDK) => ensureReady(BlazeSDK, opts).then(() => {
      const dataSource = BlazeSDK.DataSourceBuilder().ids({ids: [story.id]});
      return BlazeSDK.playContent(WSC.CONTENT_TYPE, {dataSource});
    }))
    .catch(() => openFallback(story));
};
